import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../useApi';
import type { Tenant } from '../api';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { ErrorNotice } from '../components/ErrorNotice';

export function TenantListPage() {
  const api = useApi();
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;

    api
      .get<{ tenants: Tenant[] }>('/tenants')
      .then((result) => {
        if (!cancelled) setTenants(result.tenants);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <>
      <div className="page-header">
        <h1>Acumatica instances</h1>
        <Link to="/tenants/new" className="primary button-link">
          Connect an instance
        </Link>
      </div>

      <ErrorNotice message={error} />

      {tenants === null && !error && <p className="muted">Loading…</p>}

      {tenants?.length === 0 && (
        <div className="card empty">
          <h2>No instances yet</h2>
          <p className="muted">
            Connect an Acumatica instance to start loading and cleaning up its
            master data.
          </p>
          <Link to="/tenants/new" className="primary button-link">
            Connect an instance
          </Link>
        </div>
      )}

      {tenants && tenants.length > 0 && (
        <>
        <table className="data-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Instance</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.tenantId}>
                <td>
                  <Link to={`/tenants/${tenant.tenantId}`}>{tenant.companyName}</Link>
                </td>
                <td className="muted mono">{tenant.instanceBaseUrl}</td>
                <td className="mono">{tenant.endpointVersion}</td>
                <td>
                  <ConnectionBadge
                    status={tenant.connectionStatus}
                    checkedAt={tenant.connectionCheckedAt}
                  />
                </td>
                <td>
                  {tenant.connectionStatus === 'connected' && (
                    <Link to={`/tenants/${tenant.tenantId}/entities/Customer`}>
                      Customers
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/*
          Said once, plainly. Without it the age reads as a stale-looking detail
          rather than as the point: this column reports what the last check saw.
        */}
        <p className="muted small">
          Status is from the last check, not a live test — opening this page does not
          contact your Acumatica instances. Use <strong>Test connection</strong> on an
          instance to check it now.
        </p>
        </>
      )}
    </>
  );
}
